// src/pages/Dashboard.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { format } from 'date-fns';
import { useUser } from '../contexts/UserContext'; // Import user context
import { useEvents } from '../contexts/EventContext'; // Import events context
import { useSubscription } from '../contexts/SubscriptionContext'; // Import subscription context
import SubscriptionGuard from '../components/SubscriptionGuard'; // Import subscription guard
import TripEventModal, { TripEventModalData } from '../components/modals/TripEventModal';
import {
   Box,
   Grid,
   Paper,
   Typography,
   Button,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Divider,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   Avatar,
   Chip,
   CircularProgress,
   Tabs,
   Tab,
   Tooltip,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   PhotoCamera as PhotoCameraIcon,
   Map as MapIcon,
   CloudUpload as CloudUploadIcon,
   Timeline as TimelineIcon,
   Place as PlaceIcon,
   Image as ImageIcon,
   ArrowForward as ArrowForwardIcon,
   Add as AddIcon,
   Label as LabelIcon,
   Event as EventIcon,
   Groups as GroupsIcon,
   Store as StoreIcon,
} from '@mui/icons-material';
import InvitationStatus from '../components/dashboard/InvitationStatus';
import EventInvitationStatus from '../components/dashboard/EventInvitationStatus';
import { get_dashboard_overview } from '../services';
import MapView from './MapView';
import { getDefaultBackgroundStyle, getDefaultGradient } from '../utils/defaultImages';

// Mapbox access token
mapboxgl.accessToken =
   'pk.eyJ1IjoiMjA0NXB1Ymxpc2hpbmciLCJhIjoiY203bDdtZnltMDhwMDJub2Nsenc3ZW9neiJ9.BYHR9d2yYc1Yg9mPSdisVg';

// Photo interface
interface Photo {
   id: string;
   title: string;
   thumbnailUrl: string;
   uploadedBy: string; // Add this field
   location?: {
      coordinates: [number, number]; // [longitude, latitude]
   };
   takenAt: Date;
   tags: string[];
}

// Trip interface
interface Trip {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   createdBy: string; // This maps to 'owner' in the database
   owner?: string; // Add this field to match actual database
   collaborators?: string[];
   coverPhotoId?: string;
   coverPhotoUrl?: string;
   coverImageUrl?: string;
   locations?: string[];
   tags?: string[];
   photoCount?: number; // Add this field to match actual database
   primaryDestination?: {
      name: string;
      coordinates: [number, number]; // [longitude, latitude]
   };
}

// Event interface
interface Event {
   id: string;
   title: string;
   description?: string;
   eventType: string;
   startDate: Date;
   endDate?: Date;
   userId: string; // owner of the event
   coverPhotoUrl?: string;
   photoCount?: number;
   location?: {
      name: string;
      address: string;
      coordinates: [number, number]; // [longitude, latitude]
   };
   participants?: {
      userId: string;
      email: string;
      role: string;
      status: string;
   }[];
   isPublic: boolean;
}

// Stats interface
interface Stats {
   totalPhotos: number;
   networkCount: number;
   recentUpload?: Date;
   topTags: { tag: string; count: number }[];
   tripCount: number;
   upcomingTrips: number;
   eventCount: number;
   upcomingEvents: number;
}

const Dashboard = () => {
   const { currentUser } = useUser();
   const { checkSubscriptionStatus } = useSubscription(); // Get current user
   const { events } = useEvents(); // Get events from context
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const mapContainer = useRef<HTMLDivElement>(null);
   const map = useRef<mapboxgl.Map | null>(null);
   const [recentPhotos, setRecentPhotos] = useState<any[]>([]);
   const [stats, setStats] = useState<Stats>({
      totalPhotos: 0,
      networkCount: 0,
      topTags: [],
      tripCount: 0,
      upcomingTrips: 0,
      eventCount: 0,
      upcomingEvents: 0,
   });
   const [eventInviteStatus, setEventInviteStatus] = useState({
      declinedCount: 0,
      acceptedCount: 0,
      pendingCount: 0,
   });
   const [tripInviteStatus, setTripInviteStatus] = useState({
      declinedCount: 0,
      acceptedCount: 0,
      pendingCount: 0,
   });
   const [loading, setLoading] = useState<boolean>(true);
   const [trips, setTrips] = useState<Trip[]>([]);
   const [recentTrips, setRecentTrips] = useState<any[]>([]);
   const [recentEvents, setRecentEvents] = useState<any[]>([]);
   const [invitationTabValue, setInvitationTabValue] = useState(0);

   // Modal state
   const [modalData, setModalData] = useState<TripEventModalData | null>(null);
   const [modalOpen, setModalOpen] = useState(false);

   // // Handle tab change
   const handleInvitationTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setInvitationTabValue(newValue);
   };

   // Handle opening trip modal
   const handleTripClick = (trip: any) => {
      const modalData: TripEventModalData = {
         id: trip.id,
         type: 'trip',
         title: trip.title,
         description: trip.description,
         startDate: trip.startDate ? new Date(trip.startDate) : undefined,
         endDate: trip.endDate ? new Date(trip.endDate) : undefined,
         destination: trip.primaryDestination?.name || trip.destination,
         location: trip.primaryDestination?.name || trip.destination,
         coverImageUrl: trip.coverPhotoUrl || trip.coverImageUrl,
         createdByName: trip.createdByName,
         participantCount: trip.participantCount,
         photoCount: trip.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   // Handle opening event modal
   const handleEventClick = (event: any) => {
      const modalData: TripEventModalData = {
         id: event.id,
         type: 'event',
         title: event.title,
         description: event.description,
         startDate: event.startDate ? new Date(event.startDate) : undefined,
         endDate: event.endDate ? new Date(event.endDate) : undefined,
         location: event.location?.name || event.location,
         coverImageUrl: event.coverPhotoUrl || event.coverImageUrl,
         createdByName: event.createdByName,
         participantCount: event.participantCount,
         photoCount: event.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   const fetchDashboardOverview = async () => {
      if (currentUser) {
         const overview = await get_dashboard_overview(currentUser.uid, currentUser.email || '');

         setStats((prev: Stats) => {
            return {
               ...prev,
               totalPhotos: overview.totalPhotos,
               tripCount: overview.tripCount,
               eventCount: overview.eventCount,
               networkCount: overview.networkCount,
            };
         });
         setTripInviteStatus({
            declinedCount: overview.tripInviteRejected,
            acceptedCount: overview.tripInviteAccepted,
            pendingCount: overview.tripInvitePending,
         });
         setEventInviteStatus({
            declinedCount: overview.eventInviteRejected,
            acceptedCount: overview.eventInviteAccepted,
            pendingCount: overview.eventInvitePending,
         });

         setRecentTrips(overview.recentTrips);
         setRecentEvents(overview.recentEvents);
         setRecentPhotos(overview.recentPhotos);
      }
      setLoading(false);
   };

   useEffect(() => {
      if (currentUser) {
         fetchDashboardOverview();
      }
   }, [currentUser]);

   return (
      <SubscriptionGuard redirectOnNoSubscription={false}>
         <Box
            sx={{
               flexGrow: 1,
               p: { xs: 2, md: 3 },
               width: '100%',
               maxWidth: isMobile ? '100vw' : '1200px',
               margin: '0 auto',
               overflowX: 'hidden',
               overflowY: 'auto',
               boxSizing: 'border-box',
            }}
         >
            <Grid
               container
               spacing={{ xs: 2, md: 3 }}
               sx={{
                  width: '100%',
                  m: 0,
                  boxSizing: 'border-box',
               }}
            >
               {/* Welcome message */}
               <Grid item xs={12} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper
                     sx={{
                        p: isMobile ? 3 : 3,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        borderRadius: isMobile ? 3 : 1,
                        boxShadow: isMobile ? '0 2px 12px rgba(0,0,0,0.1)' : 1,
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                     }}
                  >
                     <Box>
                        <Typography variant='h4' gutterBottom>
                           Welcome
                           {currentUser?.displayName
                              ? `, ${currentUser.displayName.split(' ')[0]}`
                              : ''}
                           !
                        </Typography>
                        <Typography variant='body1' color='text.secondary'>
                           Your travel dashboard - plan trips, manage photos, and connect with
                           friends.
                        </Typography>
                     </Box>
                     <Box
                        sx={{
                           mt: { xs: 2, md: 0 },
                           display: 'flex',
                           gap: isMobile ? 1 : 2,
                           flexDirection: isMobile ? 'column' : 'row',
                           width: isMobile ? '100%' : 'auto',
                           justifyContent: isMobile ? 'stretch' : 'flex-end',
                        }}
                     >
                        {/* New Trip button - Hidden per user request */}
                        {/* <Button
                           variant='contained'
                           startIcon={<AddIcon />}
                           onClick={() => {
                              if (checkSubscriptionStatus()) {
                                 window.location.href = '/trips';
                              }
                           }}
                           sx={{
                              borderRadius: isMobile ? 3 : 1,
                              px: isMobile ? 2.5 : 2,
                              py: isMobile ? 1.5 : 1,
                              fontSize: isMobile ? '0.875rem' : '0.875rem',
                              fontWeight: isMobile ? 500 : 500,
                              textTransform: 'none',
                              flex: isMobile ? 'none' : 'none',
                              width: isMobile ? '100%' : 'auto',
                           }}
                        >
                           New Trip
                        </Button> */}
                        <Button
                           variant='outlined'
                           startIcon={<EventIcon />}
                           onClick={() => {
                              if (checkSubscriptionStatus()) {
                                 window.location.href = '/events';
                              }
                           }}
                           sx={{
                              borderRadius: isMobile ? 3 : 1,
                              px: isMobile ? 2.5 : 2,
                              py: isMobile ? 1.5 : 1,
                              fontSize: isMobile ? '0.875rem' : '0.875rem',
                              fontWeight: isMobile ? 500 : 500,
                              textTransform: 'none',
                              flex: isMobile ? 'none' : 'none',
                              width: isMobile ? '100%' : 'auto',
                              borderColor: isMobile ? 'primary.main' : 'grey.300',
                              color: isMobile ? 'primary.main' : 'inherit',
                           }}
                        >
                           New Event
                        </Button>
                        <Button
                           variant='outlined'
                           color='primary'
                           startIcon={<ImageIcon />}
                           onClick={() => {
                              if (checkSubscriptionStatus()) {
                                 window.location.href = '/albums?create=true';
                              }
                           }}
                           sx={{
                              borderRadius: isMobile ? 3 : 1,
                              px: isMobile ? 2.5 : 2,
                              py: isMobile ? 1.5 : 1,
                              fontSize: isMobile ? '0.875rem' : '0.875rem',
                              fontWeight: isMobile ? 500 : 500,
                              textTransform: 'none',
                              flex: isMobile ? 'none' : 'none',
                              width: isMobile ? '100%' : 'auto',
                              borderColor: isMobile ? 'primary.main' : 'primary.main',
                              color: isMobile ? 'primary.main' : 'primary.main',
                           }}
                        >
                           Create Album
                        </Button>
                        {/* <Button
                        variant='outlined'
                        color="secondary"
                        component={Link}
                        to='/venues/create'
                        startIcon={<StoreIcon />}
                     >
                        Create Venue
                     </Button> */}
                     </Box>
                  </Paper>
               </Grid>


               {/* Quick Stats */}
               <Grid item xs={12} md={6} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper sx={{ p: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                     <Typography variant='h6' gutterBottom>
                        Overview
                     </Typography>
                     <Grid container spacing={2} sx={{ mt: 1, width: '100%', m: 0 }}>
                        <Grid item xs={6} sm={4}>
                           <Box sx={{ textAlign: 'center' }}>
                              <Avatar sx={{ bgcolor: 'primary.light', mb: 1, mx: 'auto' }}>
                                 <ImageIcon />
                              </Avatar>
                              <Typography variant='h4'>{stats.totalPhotos}</Typography>
                              <Typography variant='body2'>Photos</Typography>
                           </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                           <Tooltip title='Unique number of users in your network' arrow>
                              <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                                 <Avatar sx={{ bgcolor: 'secondary.light', mb: 1, mx: 'auto' }}>
                                    <GroupsIcon />
                                 </Avatar>
                                 <Typography variant='h4'>{stats.networkCount}</Typography>
                                 <Typography variant='body2'>Total Networks</Typography>
                              </Box>
                           </Tooltip>
                        </Grid>
                        {/* Trips stat - Hidden per user request */}
                        {/* <Grid item xs={6} sm={4}>
                           <Box sx={{ textAlign: 'center' }}>
                              <Avatar sx={{ bgcolor: 'warning.light', mb: 1, mx: 'auto' }}>
                                 <TimelineIcon />
                              </Avatar>
                              <Typography variant='h4'>{stats.tripCount}</Typography>
                              <Typography variant='body2'>Trips</Typography>
                           </Box>
                        </Grid> */}
                        <Grid item xs={6} sm={4}>
                           <Box sx={{ textAlign: 'center' }}>
                              <Avatar sx={{ bgcolor: 'success.light', mb: 1, mx: 'auto' }}>
                                 <EventIcon />
                              </Avatar>
                              <Typography variant='h4'>{stats.eventCount}</Typography>
                              <Typography variant='body2'>Events</Typography>
                           </Box>
                        </Grid>
                     </Grid>

                     <Divider sx={{ my: 3 }} />

                     <Tabs
                        value={invitationTabValue}
                        onChange={handleInvitationTabChange}
                        sx={{ mb: 2 }}
                     >
                        <Tab icon={<GroupsIcon />} iconPosition='start' label='Trip Invitations' />
                        <Tab icon={<EventIcon />} iconPosition='start' label='Event Invitations' />
                     </Tabs>

                     {invitationTabValue === 0 ? (
                        <InvitationStatus
                           declinedCount={tripInviteStatus.declinedCount}
                           acceptedCount={tripInviteStatus.acceptedCount}
                           pendingCount={tripInviteStatus.pendingCount}
                        />
                     ) : (
                        <EventInvitationStatus
                           declinedCount={eventInviteStatus.declinedCount}
                           acceptedCount={eventInviteStatus.acceptedCount}
                           pendingCount={eventInviteStatus.pendingCount}
                        />
                     )}

                     {/* <Typography variant='subtitle1' gutterBottom>
                     Coming up
                  </Typography>
                  <Grid container spacing={2}>
                     <Grid item xs={6}>
                        <Paper
                           variant='outlined'
                           sx={{
                              p: 2,
                              textAlign: 'center',
                              bgcolor: 'warning.light',
                              color: 'warning.contrastText',
                              borderRadius: 2,
                           }}
                        >
                           <Typography variant='h3'>{stats.upcomingTrips}</Typography>
                           <Typography variant='body2'>Upcoming Trips</Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={6}>
                        <Paper
                           variant='outlined'
                           sx={{
                              p: 2,
                              textAlign: 'center',
                              bgcolor: 'success.light',
                              color: 'success.contrastText',
                              borderRadius: 2,
                           }}
                        >
                           <Typography variant='h3'>{stats.upcomingEvents}</Typography>
                           <Typography variant='body2'>Upcoming Events</Typography>
                        </Paper>
                     </Grid>
                  </Grid> */}
                  </Paper>
               </Grid>

               {/* Mini Map */}
               <Grid item xs={12} md={6} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant='h6'>Your Travel Map</Typography>
                        <Button
                           size='small'
                           component={Link}
                           to='/map'
                           endIcon={<ArrowForwardIcon />}
                        >
                           View Full Map
                        </Button>
                     </Box>
                     <MapView height='420px' />

                     <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                           icon={<MapIcon sx={{ color: '#2196F3' }} />}
                           label='Photos'
                           size='small'
                        />
                        {/* Trips legend - Hidden per user request */}
                        {/* <Chip
                           icon={<MapIcon sx={{ color: '#FFA000' }} />}
                           label='Trips'
                           size='small'
                        /> */}
                        <Chip
                           icon={<MapIcon sx={{ color: '#9C27B0' }} />}
                           label='Events'
                           size='small'
                        />
                     </Box>
                  </Paper>
               </Grid>

               {/* Invitations */}
               {/* <Grid item xs={12} md={6}>
               <Paper sx={{ p: 2, height: '100%' }}>
                  <Tabs
                     value={invitationTabValue}
                     onChange={handleInvitationTabChange}
                     sx={{ mb: 2 }}
                  >
                     <Tab icon={<GroupsIcon />} iconPosition='start' label='Trip Invitations' />
                     <Tab icon={<EventIcon />} iconPosition='start' label='Event Invitations' />
                  </Tabs>

                  {invitationTabValue === 0 ? <InvitationStatus /> : <EventInvitationStatus />}
               </Paper>
            </Grid> */}

               {/* Recent Photos */}
               <Grid item xs={12} md={6} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper sx={{ p: 2, height: '100%', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant='h6'>Recent Photos</Typography>
                        <Button
                           size='small'
                           component={Link}
                           to='/upload'
                           startIcon={<CloudUploadIcon />}
                        >
                           Upload Photos
                        </Button>
                     </Box>

                     {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                           <CircularProgress />
                        </Box>
                     ) : recentPhotos.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                           <Typography color='text.secondary'>
                              No photos yet. Upload some to see them here!
                           </Typography>
                           <Button
                              variant='contained'
                              component={Link}
                              to='/upload'
                              startIcon={<PhotoCameraIcon />}
                              sx={{ mt: 2 }}
                           >
                              Upload Photos
                           </Button>
                        </Box>
                     ) : (
                        <Grid container spacing={2}>
                           {recentPhotos.map((photo) => (
                              <Grid item xs={4} sm={4} key={photo.id}>
                                 <Card sx={{ height: '100%' }}>
                                    <CardMedia
                                       component='img'
                                       height='140'
                                       image={photo.thumbnailUrl || ''}
                                       alt={photo.title}
                                    />
                                    <CardContent sx={{ p: 1 }}>
                                       <Typography variant='body2' noWrap>
                                          {photo.title}
                                       </Typography>
                                    </CardContent>
                                 </Card>
                              </Grid>
                           ))}
                        </Grid>
                     )}
                  </Paper>
               </Grid>

               {/* Trips Section - Hidden per user request */}
               {/* <Grid item xs={12} md={6} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper sx={{ p: 2, height: '100%', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant='h6'>Your Trips</Typography>
                        <Button
                           size='small'
                           component={Link}
                           to='/trips'
                           endIcon={<ArrowForwardIcon />}
                        >
                           View All Trips
                        </Button>
                     </Box>

                     {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                           <CircularProgress />
                        </Box>
                     ) : recentTrips.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                           <Typography color='text.secondary'>No trips yet.</Typography>
                        </Box>
                     ) : (
                        <Grid container spacing={2}>
                           {recentTrips.map((trip: any) => (
                              <Grid item xs={12} sm={6} key={trip?.id}>
                                 <Card
                                    sx={{
                                       height: '100%',
                                       display: 'flex',
                                       flexDirection: 'column',
                                       cursor: 'pointer',
                                       '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: (theme) => theme.shadows[4],
                                       },
                                       transition: 'all 0.2s ease-in-out',
                                    }}
                                    onClick={() => handleTripClick(trip)}
                                 >
                                    <Box
                                       sx={{
                                          height: 140,
                                          ...getDefaultBackgroundStyle(
                                             'trip',
                                             trip?.id,
                                             trip.coverPhotoUrl || trip.coverImageUrl
                                          ),
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          position: 'relative',
                                       }}
                                    >
                                       {!(trip.coverPhotoUrl || trip.coverImageUrl) && (
                                          <>
                                             <Box
                                                sx={{
                                                   position: 'absolute',
                                                   top: 0,
                                                   left: 0,
                                                   right: 0,
                                                   bottom: 0,
                                                   background: 'rgba(0, 0, 0, 0.3)',
                                                   zIndex: 1,
                                                }}
                                             />
                                             <Box
                                                sx={{
                                                   textAlign: 'center',
                                                   position: 'relative',
                                                   zIndex: 2,
                                                }}
                                             >
                                                <ImageIcon
                                                   sx={{
                                                      fontSize: 40,
                                                      color: 'white',
                                                      mb: 1,
                                                      opacity: 0.9,
                                                   }}
                                                />
                                             </Box>
                                          </>
                                       )}
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                       <Typography variant='h6' noWrap title={trip.title}>
                                          {trip?.title}
                                       </Typography>
                                       <Typography variant='body2' color='text.secondary'>
                                          {trip?.startDate &&
                                             new Date(trip.startDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                             })}{' '}
                                          -{' '}
                                          {trip?.endDate &&
                                             new Date(trip?.endDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                             })}
                                       </Typography>
                                       {trip?.primaryDestination && (
                                          <Box
                                             sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                                          >
                                             <PlaceIcon
                                                sx={{
                                                   fontSize: 16,
                                                   mr: 0.5,
                                                   color: 'text.secondary',
                                                }}
                                             />
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                noWrap
                                             >
                                                {trip?.primaryDestination?.name}
                                             </Typography>
                                          </Box>
                                       )}
                                    </CardContent>
                                 </Card>
                              </Grid>
                           ))}
                        </Grid>
                     )}
                  </Paper>
               </Grid> */}

               {/* Events Section */}
               <Grid item xs={12} md={6} sx={{ width: '100%', boxSizing: 'border-box' }}>
                  <Paper sx={{ p: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant='h6'>Your Events</Typography>
                        <Button
                           size='small'
                           component={Link}
                           to='/events'
                           endIcon={<ArrowForwardIcon />}
                        >
                           View All Events
                        </Button>
                     </Box>

                     {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                           <CircularProgress />
                        </Box>
                     ) : recentEvents.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                           <Typography color='text.secondary'>No events yet.</Typography>
                           {/* <Button
                           variant='contained'
                           component={Link}
                           to='/events/create'
                           startIcon={<EventIcon />}
                           sx={{ mt: 2 }}
                        >
                           Create New Event
                        </Button> */}
                        </Box>
                     ) : (
                        <Grid container spacing={2}>
                           {recentEvents.map((event) => (
                              <Grid item xs={12} sm={6} key={event.id}>
                                 <Card
                                    sx={{
                                       height: '100%',
                                       cursor: 'pointer',
                                       '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: (theme) => theme.shadows[4],
                                       },
                                       transition: 'all 0.2s ease-in-out',
                                    }}
                                    onClick={() => handleEventClick(event)}
                                 >
                                    <Box
                                       sx={{
                                          height: 140,
                                          ...(() => {
                                             const coverUrl =
                                                event?.coverPhotoUrl || event?.coverImageUrl;
                                             if (event?.title === 'moler') {
                                                console.log('🔍 Moler event data:', {
                                                   coverPhotoUrl: event?.coverPhotoUrl,
                                                   coverImageUrl: event?.coverImageUrl,
                                                   finalCoverUrl: coverUrl,
                                                });
                                             }
                                             return getDefaultBackgroundStyle(
                                                'event',
                                                event?.id,
                                                coverUrl
                                             );
                                          })(),
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          position: 'relative',
                                       }}
                                    >
                                       {!(event?.coverPhotoUrl || event?.coverImageUrl) && (
                                          <>
                                             <Box
                                                sx={{
                                                   position: 'absolute',
                                                   top: 0,
                                                   left: 0,
                                                   right: 0,
                                                   bottom: 0,
                                                   background: 'rgba(0, 0, 0, 0.3)',
                                                   zIndex: 1,
                                                }}
                                             />
                                             <Box
                                                sx={{
                                                   textAlign: 'center',
                                                   position: 'relative',
                                                   zIndex: 2,
                                                }}
                                             >
                                                <EventIcon
                                                   sx={{
                                                      fontSize: 40,
                                                      color: 'white',
                                                      mb: 1,
                                                      opacity: 0.9,
                                                   }}
                                                />
                                             </Box>
                                          </>
                                       )}
                                    </Box>
                                    <CardContent>
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: 1,
                                             mb: 1,
                                          }}
                                       >
                                          <Typography variant='h6' noWrap title={event?.title}>
                                             {event?.title}
                                          </Typography>
                                          <Chip
                                             label={event?.eventType}
                                             size='small'
                                             color='secondary'
                                          />
                                       </Box>
                                       <Typography variant='body2' color='text.secondary'>
                                          {event.startDate &&
                                             new Date(event.startDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                             })}{' '}
                                          -{' '}
                                          {event.endDate &&
                                             new Date(event.endDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                             })}
                                       </Typography>
                                       {event.location && (
                                          <Box
                                             sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                                          >
                                             <PlaceIcon
                                                sx={{
                                                   fontSize: 16,
                                                   mr: 0.5,
                                                   color: 'text.secondary',
                                                }}
                                             />
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                noWrap
                                             >
                                                {event.location.name}
                                             </Typography>
                                          </Box>
                                       )}
                                    </CardContent>
                                 </Card>
                              </Grid>
                           ))}
                        </Grid>
                     )}
                  </Paper>
               </Grid>

               {/* Tags Section */}
               {/* <Grid item xs={12}>
               <Paper sx={{ p: 2 }}>
                  <Typography variant='h6' gutterBottom>
                     Popular Tags
                  </Typography>

                  {loading ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress />
                     </Box>
                  ) : stats.topTags.length === 0 ? (
                     <Typography color='text.secondary'>
                        No tags yet. Add tags to your photos to see them here.
                     </Typography>
                  ) : (
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {stats.topTags.map(({ tag, count }) => (
                           <Chip
                              key={tag}
                              icon={<LabelIcon />}
                              label={`${tag} (${count})`}
                              component={Link}
                              to={`/manage?tag=${tag}`}
                              clickable
                              sx={{ textDecoration: 'none' }}
                           />
                        ))}
                     </Box>
                  )}
               </Paper>
            </Grid> */}
            </Grid>
         </Box>

         {/* Trip/Event Modal */}
         <TripEventModal
            open={modalOpen}
            onClose={() => {
               setModalOpen(false);
               setModalData(null);
            }}
            data={modalData}
         />
      </SubscriptionGuard>
   );
};

export default Dashboard;
